/** Chat tab — web mirror of `Sources/Views/ChatView.swift`.
 * A message list + input. Sending enqueues a `post_chat` FactoryCommand via the
 * store; the mock reflects it back as a factory_event, surfaced here as a
 * queued/ack bubble. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppState, useEnv } from '../app/context';
import { jsonDisplayString, type Json } from '../models/factory';
import { relativeShort } from '../components/UIHelpers';

interface Message {
  id: number;
  text: string;
  fromUser: boolean;
  ts: string;
}

function stringField(payload: Json, key: string): string | null {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const v = (payload as Record<string, Json | undefined>)[key];
    if (typeof v === 'string') return v;
  }
  return null;
}

export function ChatView() {
  const env = useEnv();
  const state = useAppState();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const messages = useMemo<Message[]>(() => {
    return state.snapshot.events
      .filter((e) => e.type === 'command.post_chat' || e.type === 'chat.ack')
      .sort((a, b) => Date.parse(a.event_ts) - Date.parse(b.event_ts))
      .map((ev) => {
        if (ev.type === 'command.post_chat') {
          return {
            id: ev.id,
            text: stringField(ev.payload, 'message') ?? jsonDisplayString(ev.payload),
            fromUser: true,
            ts: ev.event_ts,
          };
        }
        return {
          id: ev.id,
          text: stringField(ev.payload, 'note') ?? 'Queued.',
          fromUser: false,
          ts: ev.event_ts,
        };
      });
  }, [state.snapshot.events]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  async function send() {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    await env.submit({
      tenant_id: env.tenantId,
      kind: 'post_chat',
      payload: { message: text },
    });
  }

  return (
    <div className="chat">
      <div className="chat__scroll" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="caption" style={{ textAlign: 'center', marginTop: 40 }}>
            Send a message to steer the factory.
            <br />
            It enqueues a post_chat command.
          </div>
        ) : null}
        {messages.map((m) => (
          <div key={m.id} className={`bubble-row${m.fromUser ? ' bubble-row--user' : ''}`}>
            <div>
              <div className={`bubble ${m.fromUser ? 'bubble--user' : 'bubble--bot'}`}>{m.text}</div>
              <div className="bubble__ts" style={{ textAlign: m.fromUser ? 'right' : 'left' }}>
                {relativeShort(m.ts)}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="input-bar">
        <input
          value={draft}
          placeholder="Message the factory…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button disabled={draft.trim().length === 0} onClick={() => void send()} aria-label="Send">
          ↑
        </button>
      </div>
    </div>
  );
}
